/**
 * What an HTML page's card shows, decided once for the preview and the
 * wall's height estimate: its title over its poster (the real pixels, top
 * crop, at the page's ratio), or over a placeholder in the page's colour
 * with its first line while the poster is still missing.
 */
import type { FrameThumbnailTheme } from "@/api/frame-listing";
import { hexColor } from "@/components/shapes/shared/card-tint";
import { frameStaticExcerpt } from "@/components/shapes/shared/frame-static-excerpt";
import type { FrameData } from "@/lib/frame-data";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { previewSource } from "@/lib/frame-preview-source";
import { posterAspect, posterCover, posterUrl } from "./html-poster-theme";

/** The ratios a cover is shown at (the capture's own clamp). */
const MIN_COVER_RATIO = 0.75;
const MAX_COVER_RATIO = 1.9;
/**
 * A poster stored without its size (the old 16:10 capture), and the
 * placeholder, so the card keeps its place when the poster arrives.
 */
const DEFAULT_COVER_RATIO = 1.6;

/**
 * The title row (`html-card-preview.tsx`), in em of the card's type: its
 * padding, and `PreviewTitle` at 1.42em with 1.16 leading, at most two
 * lines, and its own 0.6em gap below (in the title's em).
 */
const TITLE_ROW_PADDING_EM = { inline: 1.6, top: 1.3, bottom: 0.3 };
const TITLE_FONT_EM = 1.42;
const TITLE_LEADING = 1.16;
const TITLE_GAP_EM = 0.6;
const TITLE_MAX_LINES = 2;
/** A display character's average width, in em of the title. */
const TITLE_CHARACTER_EM = 0.5;

/**
 * The title row's height, in em of the card's type, for `title` on a card
 * `widthEm` wide: one or two lines, by the title's length.
 */
export function htmlCardTitleEm(title: string, widthEm: number): number {
  const perLine = Math.max(
    1,
    Math.floor(
      (widthEm - 2 * TITLE_ROW_PADDING_EM.inline) /
        (TITLE_CHARACTER_EM * TITLE_FONT_EM),
    ),
  );
  const lines = Math.min(
    TITLE_MAX_LINES,
    Math.max(1, Math.ceil(title.length / perLine)),
  );
  return (
    TITLE_ROW_PADDING_EM.top +
    TITLE_FONT_EM * (lines * TITLE_LEADING + TITLE_GAP_EM) +
    TITLE_ROW_PADDING_EM.bottom
  );
}

export type HtmlCardShape =
  | {
      kind: "poster";
      title: string;
      url: string;
      /** Width / height. */
      ratio: number;
      placeholder: string | null;
    }
  | {
      kind: "placeholder";
      title: string;
      /** The page's first line, without the title it starts with. */
      line: string;
      /** The page's own colour, else the card tint hashed from the frame id. */
      tint: string | null;
      ratio: number;
    };

/** The excerpt's first line, without the headline it starts with. */
function firstLine(excerpt: string, headline: string): string {
  const first = excerpt.split("\n")[0] ?? "";
  return first.startsWith(headline)
    ? first.slice(headline.length).trim()
    : first;
}

/**
 * The stored poster a card shows as its cover, else null (the
 * placeholder). Only the app theme's own poster: the page renders in the
 * app's theme, so the other theme's capture would show a dark page in light
 * (and back). The next capture in this theme takes it.
 */
function shownCover(frame: FrameData, theme: FrameThumbnailTheme) {
  const thumbnail = frame.metadata?.thumbnail;
  const url = posterUrl(thumbnail, theme);
  const cover = posterCover(thumbnail, theme);
  if (!url) return { poster: null, cover };
  const ratio = posterAspect(thumbnail, theme) ?? DEFAULT_COVER_RATIO;
  return {
    poster: {
      kind: "poster" as const,
      url,
      ratio: Math.min(MAX_COVER_RATIO, Math.max(MIN_COVER_RATIO, ratio)),
      placeholder: cover?.placeholder ?? null,
    },
    cover,
  };
}

/** The card of HTML `frame` in the app's `theme`. */
export function htmlCardShape(
  frame: FrameData,
  theme: FrameThumbnailTheme,
): HtmlCardShape {
  const title = frameDisplayTitle(frame);
  const { poster, cover } = shownCover(frame, theme);
  if (poster) return { ...poster, title };
  const source = previewSource(frame);
  return {
    kind: "placeholder",
    title,
    line:
      cover?.line ||
      firstLine(
        frameStaticExcerpt(frame.frameType, source),
        cover?.headline || title,
      ),
    tint: cover?.tint ?? hexColor(source.themeColor),
    ratio: DEFAULT_COVER_RATIO,
  };
}

/**
 * The cover's width / height for the wall's estimate, without building the
 * placeholder's line (a thousand cards per pass).
 */
export function htmlCardRatio(
  frame: FrameData,
  theme: FrameThumbnailTheme,
): number {
  return shownCover(frame, theme).poster?.ratio ?? DEFAULT_COVER_RATIO;
}
